import { ExpandMore } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControl,
  InputAdornment,
  Typography,
  useTheme,
} from "@mui/material";
import { useProgress } from "context/ProgressProvider";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { LoanService } from "services/LoanService";
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
} from "shared/components/AppComponents";
import { ENTITY_TYPE } from "shared/helpers/Constants";
import {
  abnSchema,
  EntityTypeSchema,
  InterestOnlySchema,
  LegalEntityNameSchema,
} from "shared/helpers/Schema";
import Utils from "shared/helpers/Utils";
import { ProfileActions } from "state/profile/Action";

export const handleSaveBusiness = (data, loanId, callback, formik = null) => {
  let repaymentStatus = data.repaymentMethod === "Interest Capitalised";
  if (
    !repaymentStatus &&
    (!data.averageMonthlyTurnOver ||
      !data.totalBusinessAssets ||
      !data.totalBusinessLiabilities)
  ) {
    callback("monthly-empty");
    return;
  }
  LoanService.saveBusiness(
    {
      abn: data.abn.toString(),
      entityType: data.entityType,
      legalEntityType: data.legalEntityType,
      isMonthlyRepayment: repaymentStatus ? false : true,
      averageMonthlyTurnOver: repaymentStatus
        ? 0
        : parseFloat(String(data.averageMonthlyTurnOver).replace(/,/g, "")),
      totalBusinessAssets: repaymentStatus
        ? 0
        : parseFloat(String(data.totalBusinessAssets).replace(/,/g, "")),
      totalBusinessLiabilities: repaymentStatus
        ? 0
        : parseFloat(String(data.totalBusinessLiabilities).replace(/,/g, "")),
      isReviewConfirmed: data.isReviewConfirmed,
      isABNVerified: data.isABNVerified,
      registeredEntityName: data.registeredEntityName,
      entityAddress: data.entityAddress,
    },
    loanId,
  )
    .then((response: any) => {
      callback(response);
    })
    .catch((error: any) => {
      if (error.response?.data?.msg?.includes("already")) {
        callback(false);
      }
      Utils.handleErrorResponse(error);
    })
    .finally(() => {
      formik?.setSubmitting(false);
    });
};

export const handleCheckABN = (ev, callback) => {
  LoanService.checkABN({
    abn: ev.abn.toString(),
  })
    .then((response: any) => {
      let data = response.data.data;
      console.log(data);
      callback(data);
    })
    .catch((error: any) => {
      callback(false);
      Utils.handleErrorResponse(error);
    })
    .finally(() => {});
};

const YourBusiness = () => {
  const {
    handleCompleteAndNext,
    navigateToSectionStepHandler,
    currentProfile,
    isBroker,
  } = useProgress();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { step: urlStep } = useParams<{ step: string }>();
  const theme = useTheme();
  const authInfo = useSelector((state: any) => state.authState);

  const currentStep = urlStep ? parseInt(urlStep, 10) : 1;

  const PAGE_LENGTH = 6;
  const PAGE_ID = "intro-business";

  const isValidStep = currentStep >= 1 && currentStep < PAGE_LENGTH;

  useEffect(() => {
    if (!isValidStep) {
      navigate(`/sbl/${PAGE_ID}/1`, { replace: true });
    }
    console.log(currentStep);
  }, [currentStep, isValidStep, navigate]);

  const [businnessCheck, setBusinessCheck] = useState(
    currentProfile.isReviewConfirmed,
  );

  const handleSubmitForm = (value, formik = null) => {
    console.log(value);
    dispatch(ProfileActions.setSblProfile(value));
    const nextStep = currentStep + 1;
    const jumpStep = currentStep + 2;

    if (currentStep === 3) {
      if (currentProfile.repaymentMethod === "Interest Capitalised") {
        navigateToSectionStepHandler(PAGE_ID, jumpStep);
        return;
      }
    }
    if (nextStep < PAGE_LENGTH) {
      navigateToSectionStepHandler(PAGE_ID, nextStep);
    } else {
      if (currentProfile.isAllowedToEdit) {
        if (!currentProfile.isAllowedToEdit?.yourBusiness) {
          handleCompleteAndNext();
          return;
        }
      }

      handleSaveBusiness(
        value,
        currentProfile.loadId,
        (data) => {
          if (data === "monthly-empty") {
            navigate("/sbl/intro-business/4");
            return;
          }
          if (data) handleCompleteAndNext();
        },
        formik,
      );
    }
  };

  if (!isValidStep) {
    return null;
  }

  return (
    <>
      {currentStep === 1 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={abnSchema}
            validateOnMount
            onSubmit={(value, formik) => {
              if (currentProfile.isAllowedToEdit) {
                if (!currentProfile.isAllowedToEdit?.yourBusiness) {
                  handleSubmitForm(value);
                  return;
                }
              }
              formik.setSubmitting(true);

              handleCheckABN(value, (data) => {
                if (data) {
                  formik.setSubmitting(false);
                  dispatch(
                    ProfileActions.setSblProfile({
                      ...data,
                      entityAddress: `${data.postcode} ${data.state}`,
                    }),
                  );
                  handleSubmitForm(value);
                }
                formik.setSubmitting(false);
              });
            }}
            initialValues={{
              abn: currentProfile.abn,
            }}
          >
            {({ handleSubmit, errors, isSubmitting }) => (
              <form autoComplete="off">
                <Typography variant="h2">
                  {isBroker ? "Business details" : "Your business"}
                </Typography>
                <Typography variant="h3" mt={3}>
                  {isBroker
                    ? "What is your customer's ABN?"
                    : "What is your ABN?"}
                </Typography>
                <AppFormikTextField
                  placeholder="Enter ABN number"
                  label=""
                  name="abn"
                  outerLabel
                  className="ezifin-default-field"
                  removeSpacing
                  returnAsString
                />

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={isSubmitting}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0 || isSubmitting}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}

      {currentStep === 2 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={EntityTypeSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              entityType: currentProfile.entityType,
            }}
          >
            {({ handleSubmit, errors }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Business details" : "Your business"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    {isBroker
                      ? "What is their entity type?"
                      : "What is your entity type?"}
                  </Typography>

                  <Box mt={2}>
                    <FormControl sx={{ width: 325 }}>
                      <AppFormikSelect
                        placeholder="Select"
                        name="entityType"
                        items={ENTITY_TYPE}
                        valueKey="value"
                        titleKey="label"
                        outerLabel
                        iconComponent={ExpandMore}
                        className="ezifin-default-field"
                      />
                    </FormControl>
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 3 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={LegalEntityNameSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              legalEntityType: currentProfile.legalEntityType,
            }}
          >
            {({ handleSubmit, errors }) => (
              <form autoComplete="off">
                <Typography variant="h2">
                  {isBroker ? "Business details" : "Your business"}
                </Typography>
                <Typography variant="h3" mt={3}>
                  {isBroker
                    ? "What is your customer legal entity name?"
                    : "What is your legal entity name?"}
                </Typography>
                <AppFormikTextField
                  placeholder="Legal entity name"
                  label=""
                  name="legalEntityType"
                  outerLabel
                  className="ezifin-default-field"
                  allowComma
                />

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={false}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}

      {currentStep === 4 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={InterestOnlySchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              averageMonthlyTurnOver:
                currentProfile.averageMonthlyTurnOver === 0
                  ? null
                  : Utils.formatCurrency(currentProfile.averageMonthlyTurnOver),
              totalBusinessAssets:
                currentProfile.totalBusinessAssets === 0
                  ? null
                  : Utils.formatCurrency(currentProfile.totalBusinessAssets),
              totalBusinessLiabilities:
                currentProfile.totalBusinessLiabilities === 0
                  ? null
                  : Utils.formatCurrency(
                      currentProfile.totalBusinessLiabilities,
                    ),
            }}
          >
            {({ values, setFieldValue, handleSubmit, errors }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Business details" : "Your business"}
                  </Typography>
                  <Typography variant="h3" mt={3} fontWeight={400}>
                    {isBroker
                      ? "Please tell us a little bit more about their business"
                      : "Please tell us a little bit more about your business"}
                  </Typography>
                  <Box mt={3}>
                    <Typography variant="h3" mt={3}>
                      {isBroker
                        ? "What is their average monthly turnover?"
                        : "What is your average monthly turnover?"}
                    </Typography>
                    <AppFormikTextField
                      placeholder="Average monthly turnover"
                      label=""
                      name="averageMonthlyTurnOver"
                      outerLabel
                      className="ezifin-default-field"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        },
                      }}
                      customIsoDollarDecimal
                    />
                  </Box>
                  <Box mt={3}>
                    <Typography variant="h3" mt={3}>
                      Total business assets value
                    </Typography>
                    <AppFormikTextField
                      label=""
                      placeholder="Total business assets"
                      name="totalBusinessAssets"
                      outerLabel
                      className="ezifin-default-field"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        },
                      }}
                      customIsoDollarDecimal
                    />
                  </Box>
                  <Box mt={3}>
                    <Typography variant="h3" mt={3}>
                      Total business liabilities value
                    </Typography>
                    <AppFormikTextField
                      label=""
                      placeholder="Total business liabilities"
                      name="totalBusinessLiabilities"
                      outerLabel
                      className="ezifin-default-field"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        },
                      }}
                      customIsoDollarDecimal
                    />
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 5 && (
        <Box
          sx={{
            width: { xs: "calc(100% - 17.5vw)", md: "100%" },
          }}
        >
          <Formik
            enableReinitialize
            validationSchema={abnSchema}
            validateOnMount
            onSubmit={(value, formik) => {
              if (!businnessCheck) {
                let message = isBroker
                  ? "Please review and confirm their business details before proceeding."
                  : "Please review and confirm your business details before proceeding.";
                Utils.handleMessageResponse(message);
                formik.setSubmitting(false);
                return;
              }
              if (currentProfile.isAllowedToEdit) {
                if (!currentProfile.isAllowedToEdit?.yourBusiness) {
                  handleSubmitForm(value);
                  return;
                }
              }
              formik.setSubmitting(true);

              handleCheckABN(value, (data) => {
                if (data) {
                  handleSubmitForm(
                    {
                      ...currentProfile,
                      abn: value.abn,
                      registeredEntityName: data.registeredEntityName,
                      entityAddress: `${data.postcode} ${data.state}`,
                    },
                    formik,
                  );
                } else {
                  formik.setSubmitting(false);
                }
              });
            }}
            initialValues={{
              abn: currentProfile.abn,
            }}
          >
            {({ handleSubmit, errors, isSubmitting }) => (
              <form autoComplete="off">
                <Typography variant="h2">
                  {isBroker ? "Business details" : "Your business"}
                </Typography>
                <Typography variant="h3" mt={3}>
                  {isBroker ? "ABN" : "What is your ABN?"}
                </Typography>
                <AppFormikTextField
                  placeholder="Enter ABN number"
                  label=""
                  name="abn"
                  outerLabel
                  className="ezifin-default-field"
                  removeSpacing
                  returnAsString
                />

                <Box
                  mt={3}
                  sx={{
                    padding: "12px",
                    border: "1px solid #e7e7e8",
                    borderRadius: "6px",
                    backgroundColor: "#f3f3f5",
                  }}
                >
                  <Typography variant="h3" fontWeight={400}>
                    {currentProfile.legalEntityType.replace(/\b\w/g, (c) =>
                      c.toUpperCase(),
                    )}
                  </Typography>
                  <br />
                  <Typography variant="h3" fontWeight={400}>
                    Name: {currentProfile.registeredEntityName}
                  </Typography>
                  <Typography variant="h3" fontWeight={400}>
                    Address: {currentProfile.entityAddress}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    textAlign: "left",
                    marginTop: "1.5rem",
                    width: "calc(463.5px + 1rem)",
                    display: "flex",
                    alignItems: "center",
                    [theme.breakpoints.down("md")]: {
                      width: "inherit",
                      minWidth: "95vw",
                    },
                  }}
                >
                  <Checkbox
                    checked={businnessCheck}
                    onChange={(e) => {
                      console.log(e.target.value);
                      dispatch(
                        ProfileActions.setSblProfile({
                          isReviewConfirmed:
                            e.target.value === "on" ? true : false,
                        }),
                      );
                      setBusinessCheck((prev) => !prev);
                    }}
                  />
                  <Typography
                    variant="h4"
                    fontWeight={400}
                    lineHeight={"20px"}
                    sx={{ maxWidth: "90%" }}
                  >
                    {isBroker
                      ? "I have reviewed the details of my customer’s business"
                      : "I have reviewed the details of my business"}
                  </Typography>
                </Box>

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    disabled={Object.keys(errors).length > 0 || isSubmitting}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}
    </>
  );
};

export default YourBusiness;
